package com.islamicmedia.shared.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt

/**
 * A single subscription plan rendered on the paywall. Admin-editable via /admin → Pricing, served
 * to every client by GET /api/pricing. The [key] is the Stripe plan key handed to /api/checkout —
 * the rest is pure display, so prices/copy can change without an app release.
 */
@Serializable
data class PricingPlan(
    /** Stripe plan key sent to /api/checkout (e.g. "basic_10", "pro_30"). */
    val key: String,
    /** Plan name shown to the user, e.g. "Starter". */
    val name: String,
    /** Display price string, e.g. "$9 USD/mo". Rendered verbatim — currency/period are baked in. */
    val price: String,
    /** Optional previous price, rendered struck-through next to [price]. Null → no strikethrough. */
    @SerialName("old_price") val oldPrice: String? = null,
    /** Extra marketing bullets shown after the entitlement summary. Optional. */
    val features: List<String> = emptyList(),
    /** Optional highlight, e.g. "Best value". Non-null → gold ribbon (mobile) / featured column (web). */
    val badge: String? = null,
    /**
     * What the plan grants. When true, subscribers get UNLIMITED analyses (server sets
     * `unlimited_until` to the period end each renewal) and [fullCredits]/[quickCredits] are ignored.
     * When false, the plan grants that many full + quick analyses per renewal.
     */
    val unlimited: Boolean = false,
    @SerialName("full_credits") val fullCredits: Int = 0,
    @SerialName("quick_credits") val quickCredits: Int = 0,
) {
    /**
     * The "what you get" lines derived from the entitlement config, shown before [features]. A plan
     * flagged [unlimited] says nothing here: we no longer promise "unlimited" anywhere. The weekly
     * allowance is stated by a server-written first feature line ("Up to N searches every week",
     * see the server's PricingFeed) on the MONTHLY plan only, so it always matches the live
     * fair-use cap, which applies to monthly subscribers only (the annual plan gets no such line).
     * Otherwise the full/quick counts.
     */
    val entitlementLines: List<String>
        get() = if (unlimited) emptyList()
        else buildList {
            if (fullCredits > 0) add("$fullCredits full ${if (fullCredits == 1) "analysis" else "analyses"}")
            if (quickCredits > 0) add("$quickCredits quick ${if (quickCredits == 1) "search" else "searches"}")
        }

    /** All lines under the plan name: entitlement summary first, then any marketing bullets. */
    val displayLines: List<String> get() = entitlementLines + features

    /**
     * [price] spread over 12 months, e.g. "$ 149 USD" → "$12.42". Null when the price doesn't
     * parse to a number.
     *
     * Only meaningful for an ANNUAL plan — the caller decides whether that applies. Derived rather
     * than written into the paywall copy because the admin-editable price has changed twice and
     * each time left a hardcoded "$12.50/month" contradicting the price right above it.
     */
    val perMonthFromAnnual: String?
        get() {
            val total = price.firstNumberOrNull() ?: return null
            val cents = ((total / 12.0) * 100).roundToInt()
            return "$${cents / 100}.${(cents % 100).toString().padStart(2, '0')}"
        }

    /**
     * "Save X%" derived from [oldPrice] vs [price] when both parse to a number and the old price is
     * higher. Null when there's no old price or the strings aren't comparable numbers — callers just
     * hide the pill in that case.
     */
    val savePercent: Int?
        get() {
            val now = price.firstNumberOrNull() ?: return null
            val old = oldPrice?.firstNumberOrNull() ?: return null
            return if (old > now && old > 0.0) (((old - now) / old) * 100).roundToInt() else null
        }
}

/**
 * The full paywall pricing config. [plans] holds 1–3 plans in display order. Clients fall back to
 * [DEFAULT] whenever the server config is unreachable or has no plans, so the paywall is never blank.
 */
@Serializable
data class PricingConfig(
    val plans: List<PricingPlan> = emptyList(),
    /** Optional bump-on-save marker (updated_at epoch). Informational only. */
    val version: Long? = null,
    /**
     * Which checkout the paywall sends people to — admin-switchable so a promotion can be turned on
     * and off without an app release.
     *
     *  - `"stripe"`     — the in-app Stripe checkout on [PAYWALL_DIRECT_PLAN_KEY] (pro_29).
     *  - `"thrivecart"` — the externally-hosted ThriveCart offer at [thriveCartUrl], which grants
     *                     the thrivecart_9 plan through its own webhook.
     *
     * Defaults to "stripe", which is also what an OLDER app build does with this field absent —
     * so switching to thrivecart only affects clients new enough to understand it, and never
     * strands an old one on a mode it can't render.
     */
    @SerialName("paywall_mode") val paywallMode: String = MODE_STRIPE,
    /** Checkout URL for [MODE_THRIVECART]. Ignored in stripe mode. */
    @SerialName("thrivecart_url") val thriveCartUrl: String? = null,
    /**
     * How many full analyses a registered, non-paying account gets free (admin Limits page, default 3),
     * sent by GET /api/pricing so a signed-out visitor is promised the right number. Null from a server
     * that predates the setting: clients then use [DEFAULT_FREE_FULL_LIMIT].
     */
    @SerialName("free_full_limit") val freeFullLimit: Int? = null,
) {
    val isThriveCartMode: Boolean get() =
        paywallMode == MODE_THRIVECART && !thriveCartUrl.isNullOrBlank()

    companion object {
        const val MODE_STRIPE = "stripe"
        const val MODE_THRIVECART = "thrivecart"

        /**
         * Pre-fill the buyer's details into the ThriveCart checkout.
         *
         * This is the whole reason the in-app path can be trusted: the user is already signed in,
         * but ThriveCart asks for an email of its own, and an entitlement granted to a DIFFERENT
         * address than their account leaves them paying with no access — the exact stranding that
         * had to be fixed by hand for the first buyers. Passing the signed-in address means the
         * field is already correct before they can mistype it.
         *
         * Parameter names are ThriveCart's documented pre-fill format
         * (support.thrivecart.com/help/pre-fill-your-checkout-fields).
         */
        fun thriveCartCheckoutUrl(
            baseUrl: String, email: String?, firstName: String? = null, lastName: String? = null,
        ): String {
            val params = buildList {
                email?.trim()?.takeIf { it.isNotBlank() }?.let { add("passthrough[customer_email]" to it) }
                firstName?.trim()?.takeIf { it.isNotBlank() }?.let { add("passthrough[customer_firstname]" to it) }
                lastName?.trim()?.takeIf { it.isNotBlank() }?.let { add("passthrough[customer_lastname]" to it) }
            }
            if (params.isEmpty()) return baseUrl
            val sep = if (baseUrl.contains('?')) "&" else "?"
            return baseUrl + sep + params.joinToString("&") { (k, v) ->
                "${k.urlEncodeComponent()}=${v.urlEncodeComponent()}"
            }
        }

        /**
         * The path ThriveCart redirects to once payment succeeds.
         *
         * Lives here rather than in either UI so the embedded checkout on mobile and the iframe on
         * web recognise "paid" by the same rule and cannot drift apart.
         */
        const val THRIVECART_SUCCESS_PATH = "/thrivecart-thank-you"

        /**
         * True once the embedded checkout has navigated to our own post-payment page.
         *
         * This is the ONLY signal the app gets that a purchase completed. ThriveCart owns the
         * checkout, so there is no callback and no result code — the redirect is the event. Matched
         * on path only: the redirect carries `thrivecart[...]` query parameters, and it arrives on
         * whichever host the build is pointed at.
         *
         * Note what this does NOT mean: access has been granted. That happens server-side when
         * ThriveCart's webhook lands, which can be a second or two later. This says "stop showing
         * the checkout and start looking for the grant", nothing more.
         */
        fun isThriveCartSuccessUrl(url: String?): Boolean {
            val u = url?.trim().orEmpty()
            if (u.isBlank()) return false
            val afterScheme = u.substringAfter("://", u)
            val path = afterScheme.substringAfter('/', "").substringBefore('?').substringBefore('#')
            return "/$path".trimEnd('/') == THRIVECART_SUCCESS_PATH
        }

        /** Built-in fallback — shown when /api/pricing returns empty. Keys must match StripeService.plans. */
        val DEFAULT = PricingConfig(
            plans = listOf(
                PricingPlan(
                    key = "annual",
                    name = "Annual",
                    price = "$9 USD/mo",
                    unlimited = true,
                ),
                PricingPlan(
                    key = "pro_29",
                    name = "Monthly",
                    price = "$29 USD/mo",
                    unlimited = true,
                    badge = "Best value",
                ),
            )
        )
    }
}

/**
 * Percent-encode a query-string component. Hand-rolled because the bracketed ThriveCart parameter
 * names (`passthrough[customer_email]`) and the `@` in an address both have to survive intact into
 * a URL, and this runs in commonMain where java.net.URLEncoder isn't available.
 */
private fun String.urlEncodeComponent(): String {
    val unreserved = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.~"
    val out = StringBuilder()
    for (b in this.encodeToByteArray()) {
        val c = b.toInt().toChar()
        if (c in unreserved) out.append(c)
        else out.append('%').append(((b.toInt() and 0xFF).toString(16)).padStart(2, '0').uppercase())
    }
    return out.toString()
}

/** Parse the first contiguous number out of a price string ("$29 USD/mo" → 29.0, "$9.99" → 9.99). */
private fun String.firstNumberOrNull(): Double? {
    val sb = StringBuilder()
    for (c in this) {
        if (c.isDigit() || (c == '.' && sb.isNotEmpty())) sb.append(c)
        else if (sb.isNotEmpty()) break // stop at the first gap once we've started collecting
    }
    return sb.toString().toDoubleOrNull()
}
